import { Banknote } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useUser } from '@/shared/hooks/useAuth';
import { cn } from '@/shared/utils/utils';
import { AdminIncentiveView, EmployeeIncentiveView } from './IncentivesPage';
import { AdminWarningsView, EmployeeWarningsView } from './PerformancePage';

type TabId = 'targets' | 'warnings';

const TABS: { id: TabId; label: string }[] = [
  { id: 'targets', label: 'Targets & Achievement' },
  { id: 'warnings', label: 'Warnings' },
];

/**
 * Merged "Performance & Incentives" menu (2026-08-16) — previously two
 * separate nav items (Performance, Incentives) each re-deriving the same
 * isAdmin check and rendering an almost-identical admin/employee split. One
 * page, two tabs: Targets & Achievement (sales target/incentive tracking)
 * and Warnings (performance issues). A third "Scores" tab was scoped in
 * planning but deliberately left out entirely — no scoring mechanism exists
 * yet, and a visible-but-disabled tab would be more confusing than omitting
 * it until there's something to show.
 */
export function PerformanceIncentivesPage() {
  const user = useUser();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin' || (user?.department === 'hr' && ['manager', 'team_lead'].includes(user?.role ?? ''));
  const [tab, setTab] = useState<TabId>('targets');

  return (
    <div className="space-y-4 pb-16">
      <PageHeader icon={Banknote} eyebrow="People" title="Performance & Incentives" description={<>{isAdmin
            ? 'Assign revenue targets, track achievement, calculate incentive payouts, and manage employee warnings.'
            : 'Track your targets, incentive earnings, and any warnings issued to you.'}</>} />

      <div className="flex gap-1 border-b border-outline/50">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'targets'
        ? (isAdmin ? <AdminIncentiveView /> : <EmployeeIncentiveView />)
        : (isAdmin ? <AdminWarningsView /> : <EmployeeWarningsView />)}
    </div>
  );
}
